import React, { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { buildingsApi, clientsApi, Client } from '../services/api';
import type { BuildingCreateError, DuplicateCandidate } from '../services/api';
import { Building } from '../types';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { InputField } from '../ds/InputField';
import { ModalShell } from '../ds/ModalShell';
import { ConfirmModal } from '../ds/ConfirmModal';
import { useCeebImportPolling } from '../hooks/useCeebImportPolling';

// Leaflet pod bundlerem CRA gubi domyślne ikony markera (znany problem) — wskazujemy je jawnie.
L.Icon.Default.mergeOptions({ iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow });

// Typ strukturalny budynku — radio w modalu. Autorytatywny TYLKO dla budynków
// spoza CEEB; dla znalezionych import nadpisze go wartością z CEEB (D8/F4).
type BuildingType = 'jednolokalowy' | 'wielolokalowy';

type FormState = {
  clientId: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  // „Oznaczenie specjalne" (§16, 2026-08-04) — opcjonalna nazwa własna widoczna przy adresie.
  specialMarking: string;
  // 045 (§15, 2026-08-04): punkt budynku (WGS84) z mapki — zasila krok mapy rejestracji CEEB.
  geoLat: number | null;
  geoLng: number | null;
  type: BuildingType;
  // Tri-state: true/false = odpowiedź CEEB (tylko wielolokalowy), null = brak wyboru / nie dotyczy.
  businessActivity: boolean | null;
};

const emptyForm: FormState = {
  clientId: '',
  street: '',
  houseNumber: '',
  postalCode: '',
  city: '',
  specialMarking: '',
  geoLat: null,
  geoLng: null,
  type: 'jednolokalowy',
  businessActivity: null,
};

// Faza maszyny stanu modala:
//  idle      — formularz,
//  checking  — sprawdzanie duplikatu adresu (#25),
//  creating  — POST /buildings w toku,
//  importing — inspekcja PLANNED utworzona, trwa pobranie z CEEB,
//  done/error — terminal (komunikat + „Zamknij").
type Phase = 'idle' | 'checking' | 'creating' | 'importing' | 'done' | 'error';

type ResultMsg = { text: string; severity: 'success' | 'info' | 'error' };
// Istniejący budynek pod wpisanym adresem — z GET /duplicates (`exact`) albo z 409 POST-a (`existing`).
type DuplicateEntry = Pick<DuplicateCandidate, 'id' | 'address' | 'ceeb_id' | 'match'>;
const withCeeb = (d: DuplicateEntry) => `${d.address}${d.ceeb_id ? ` (CEEB ${d.ceeb_id})` : ''}`;

const TYPE_OPTIONS: Array<{ value: BuildingType; label: string }> = [
  { value: 'jednolokalowy', label: 'Jednolokalowy' },
  { value: 'wielolokalowy', label: 'Wielolokalowy' },
];

// Pytanie CEEB „Czy prowadzona jest działalność gospodarcza?" — TYLKO dla wielolokalowych.
const BUSINESS_ACTIVITY_OPTIONS: Array<{ value: boolean; label: string }> = [
  { value: true, label: 'Tak' },
  { value: false, label: 'Nie' },
];

export interface AddBuildingModalProps {
  open: boolean;
  onClose: () => void;
  /** Wołane po utworzeniu budynku — także przy błędzie/timeout importu i w ścieżce
   *  sieroty (budynek JUŻ istnieje, lista musi się odświeżyć). NIE wołane, gdy POST
   *  /buildings padł (nic nie powstało). */
  /** Wołane RAZ po utworzeniu budynku (pkt 26: rodzic pokazuje go w etapie NOWY po adresie). */
  onCreated: (created: { id: string; address: string }) => void;
  /** Duplikat adresu: rodzic pokazuje istniejący budynek zamiast tworzyć kopię
   *  (właściciel 2026-09-30 — kopia Jana Sikory 21 powstała jednym kliknięciem „Utwórz mimo to";
   *  od #426, 2026-10-16, duplikatu nie da się już założyć — serwer odpowiada 409). */
  onShowExisting?: (existing: { id: string; address: string }) => void;
}

/**
 * AddBuildingModal — „Dodaj budynek" (create → import CEEB → poll). Od 2026-09-13 (pkt 26)
 * NIE zakłada przeglądu — nowy budynek jest NOWY, przegląd powstaje przez [Zaplanuj].
 * Wzorce: ClientsTab `ModalShell`/`InputField`/saving-gate; radio typu = button-group
 * z BuildingInspectionView; polling przez `useCeebImportPolling` (Z.12).
 *
 * (F3) 2 wywołania (create / import) są NIETRANSAKCYJNE — obsługujemy partial-failure
 * jawnie: create-fail = nic nie powstało (brak onCreated); import done/error = budynek JUŻ
 * powstał → zawsze onCreated({ id, address }).
 */
function AddBuildingModal({ open, onClose, onCreated, onShowExisting }: AddBuildingModalProps) {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [phase, setPhase] = useState<Phase>('idle');
  const [resultMsg, setResultMsg] = useState<ResultMsg | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  // #25: istniejące budynki pod tym samym adresem — null = nie sprawdzano / adres czysty.
  // Ustawione ⇒ blokada: primary w stopce to „Otwórz istniejący". `match: 'words'` = ulica skrócona
  // („Niepokólczyckiego 48" ↔ „płk. Franciszka Niepokólczyckiego 48") — blokuje tak samo jak ten
  // sam adres. #426: inną nieruchomość pod tym adresem (oficyna, „Mała Zamkowa 5" obok „Zamkowa 5")
  // da się dodać tylko świadomie — „To inna nieruchomość — dodaj mimo to" → ConfirmModal z nazwą
  // istniejącego budynku → POST z `confirmDuplicate: true` (serwer zapisuje to w audycie).
  const [duplicates, setDuplicates] = useState<DuplicateEntry[] | null>(null);
  // Otwarte pytanie „dodać mimo to?" (ConfirmModal) — dopiero jego potwierdzenie wysyła confirmDuplicate.
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  // #426: komunikat serwera z 409 BUILDING_DUPLICATE_ADDRESS (wyścig albo sprawdzenie fail-open).
  const [duplicateMsg, setDuplicateMsg] = useState<string | null>(null);
  // Pkt 27: podpowiedź „czy chodziło o…” (podobne adresy z pg_trgm) — nie blokuje dodania.
  const [similar, setSimilar] = useState<DuplicateCandidate[]>([]);
  // Ref, bo handleSubmit czyta wynik zaraz po setSimilar (stan React jeszcze stary).
  const similarRef = useRef<DuplicateCandidate[]>([]);

  const { status: importStatus, result: importResult, start } = useCeebImportPolling();

  // Czekamy na terminal importu (done/error) tylko po faktycznym wystartowaniu (krok 3).
  const awaitingImportRef = useRef(false);
  // onCreated wołane DOKŁADNIE RAZ na sesję modala (guard sieroty + terminala).
  const onCreatedFiredRef = useRef(false);
  // Zadanie 5.6: id świeżo utworzonego budynku — potrzebne, by po imporcie wystartować Fazę A.
  const createdBuildingIdRef = useRef<string | null>(null);

  // 045 (§15): mapka punktu (Leaflet + OSM). Instancja żyje w refach — Leaflet nie jest
  // reactowy; init przy rozwinięciu sekcji, sprzątanie przy zamknięciu modala.
  const [showMap, setShowMap] = useState(false);
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!showMap || !mapDivRef.current || mapRef.current) return;
    const map = L.map(mapDivRef.current).setView([50.312, 18.672], 12); // okolice Zabrza/Gliwic
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    map.on('click', (e: L.LeafletMouseEvent) => {
      const lat = Number(e.latlng.lat.toFixed(6));
      const lng = Number(e.latlng.lng.toFixed(6));
      setForm((prev) => ({ ...prev, geoLat: lat, geoLng: lng }));
      if (markerRef.current) markerRef.current.setLatLng(e.latlng);
      else markerRef.current = L.marker(e.latlng).addTo(map);
    });
    mapRef.current = map;
    return undefined;
  }, [showMap]);

  useEffect(() => {
    if (open) return;
    // zamknięcie modala = pełne sprzątnięcie instancji mapy
    setShowMap(false);
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; markerRef.current = null; }
  }, [open]);

  const clearGeoPoint = () => {
    setForm((prev) => ({ ...prev, geoLat: null, geoLng: null }));
    if (markerRef.current && mapRef.current) { markerRef.current.remove(); markerRef.current = null; }
  };

  // Reset stanu przy każdym otwarciu.
  useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setFieldErrors({});
    setPhase('idle');
    setResultMsg(null);
    setDuplicates(null);
    setDuplicateMsg(null);
    setConfirmDuplicate(false);
    awaitingImportRef.current = false;
    onCreatedFiredRef.current = false;
    createdBuildingIdRef.current = null;
  }, [open]);

  // Lista klientów do selecta.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    clientsApi
      .getAll()
      .then((data) => { if (alive) setClients(data); })
      .catch(() => { if (alive) setClients([]); });
    return () => { alive = false; };
  }, [open]);

  const fireOnCreated = useCallback(() => {
    if (onCreatedFiredRef.current) return;
    onCreatedFiredRef.current = true;
    onCreated({
      id: createdBuildingIdRef.current || '',
      address: `${form.street.trim()} ${form.houseNumber.trim()}`.trim(),
    });
  }, [onCreated, form.street, form.houseNumber]);

  // Reakcja na terminal importu (Z.12). Budynek JUŻ powstał niezależnie od wyniku importu
  // → po done LUB error zawsze odświeżamy listę (F3).
  useEffect(() => {
    if (!awaitingImportRef.current) return;
    if (importStatus !== 'done' && importStatus !== 'error') return;
    awaitingImportRef.current = false;

    // Zadanie 5.6 (2026-08-20): po imporcie startuje FAZA A — znalezienie protokołu tego
    // cyklu, „Wyślij" inwentaryzacji (CEEB kopiuje wtedy lokale z poprzedniej kontroli) i zapis
    // numeru sprawy. PO imporcie, nie równolegle: obie rzeczy jadą na jednej sesji CEEB.
    //
    // ⚠️ 2026-08-29: TU BUDYNEK W CEEB NIE POWSTAJE. Wołamy Fazę A bez `allowCreate`, więc gdy
    // adresu nie ma jeszcze w rejestrze, krok „protokół" kończy się miękko, a budynek zostaje
    // założony dopiero przy „Wyślij do CEEB" — gdy mamy już dane z terenu. Decyzja właściciela:
    // omyłkowo wpisany adres nie może zostawić pustego budynku w rejestrze państwowym.
    //
    // Świadomie fire-and-forget: budynek i inspekcja już istnieją, więc niepowodzenie Fazy A
    // NIE MOŻE zablokować dodania budynku. Stan (i przycisk „Ponów") pokazuje CeebPhaseAPanel
    // na karcie budynku — dokładnie tego wymaga 5.6 („budynek zostaje u nas z informacją").
    if (createdBuildingIdRef.current) {
      // Panel na karcie budynku przejmie stan TYLKO wtedy, gdy backend zdążył założyć status-file.
      // Gdy POST padnie WCZEŚNIEJ (500, timeout, 403 scope), pliku nie ma, `phase-a-status` zwraca
      // `idle`, a panel milczy — użytkownik zobaczyłby „Zaciągnięto z CEEB…" i nigdy się nie
      // dowiedział, że budynek został bez protokołu i bez numeru sprawy (review 2026-08-20, pkt 5).
      void buildingsApi.startPhaseA(createdBuildingIdRef.current).catch((e: any) => {
        setResultMsg({
          text: `Budynek dodano, ale nie udało się uruchomić przygotowania w CEEB (${e?.message || 'błąd'}). `
            + 'Otwórz kartę budynku i użyj „Ponów przygotowanie w CEEB".',
          severity: 'error',
        });
      });
    }

    if (importStatus === 'done') {
      if (importResult?.found) {
        // UWAGA: źródła ciepła z CEEB zapisane RELACYJNIE per-lokal — w DB, ale jeszcze
        // niewidoczne w prefill-UI dla świeżego budynku (D8/F1).
        setResultMsg({
          text: `Zaciągnięto z CEEB: ${importResult?.locals ?? 0} lokali / ${importResult?.heatSources ?? 0} źródeł.`,
          severity: 'success',
        });
      } else {
        // Właściciel 2026-10-02: „nigdy niekontrolowany" czytało się jak „nie ma go w CEEB", a import
        // przeszukuje tylko protokoły NASZEJ firmy (zone-api). Czy adres jest w rejestrze, sprawdza
        // dopiero bot przed rejestracją — i to mówimy wprost.
        setResultMsg({
          text: 'Nie znaleziono w protokołach naszej firmy — bot sprawdzi rejestr CEEB przed rejestracją. '
            + 'Budynek utworzono z domyślnymi danymi.',
          severity: 'info',
        });
      }
      setPhase('done');
    } else {
      setResultMsg({
        text: 'Budynek utworzono, ale pobranie danych z CEEB nie powiodło się — użyj „Odśwież z CEEB" później.',
        severity: 'error',
      });
      setPhase('error');
    }
    fireOnCreated();
  }, [importStatus, importResult, fireOnCreated]);

  const busy = phase === 'checking' || phase === 'creating' || phase === 'importing';
  const terminal = phase === 'done' || phase === 'error';

  const setField = (key: keyof FormState, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key]) setFieldErrors((e) => ({ ...e, [key]: undefined }));
    // Zmiana adresu unieważnia poprzednie ostrzeżenie o duplikacie (#25).
    if (duplicates) setDuplicates(null);
    setDuplicateMsg(null);
    setConfirmDuplicate(false);
    if (similar.length) setSimilar([]);
    similarRef.current = [];
  };

  // Typ jest sprzężony z „działalność gospodarcza": przy powrocie do `jednolokalowy`
  // zerujemy wybór, żeby nie wysłać nieaktualnego true/false (pytanie CEEB nie dotyczy).
  const setType = (value: BuildingType) => {
    setForm((f) => ({
      ...f,
      type: value,
      businessActivity: value === 'jednolokalowy' ? null : f.businessActivity,
    }));
    if (fieldErrors.type) setFieldErrors((e) => ({ ...e, type: undefined }));
    if (value === 'jednolokalowy' && fieldErrors.businessActivity) {
      setFieldErrors((e) => ({ ...e, businessActivity: undefined }));
    }
  };

  const setBusinessActivity = (value: boolean) => {
    setForm((f) => ({ ...f, businessActivity: value }));
    if (fieldErrors.businessActivity) setFieldErrors((e) => ({ ...e, businessActivity: undefined }));
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof FormState, string>> = {};
    if (!form.street.trim()) errs.street = 'Ulica jest wymagana';
    // Właściciel 2026-09-13 (pkt 26): przy jednolokalowym numer nie jest obowiązkowy (dom bez numeru
    // w rejestrze zdarza się; adres identyfikuje ulica + miasto). Wielolokalowy nadal wymaga numeru.
    if (form.type === 'wielolokalowy' && !form.houseNumber.trim()) errs.houseNumber = 'Numer jest wymagany';
    if (!form.postalCode.trim()) errs.postalCode = 'Kod pocztowy jest wymagany';
    if (!form.city.trim()) errs.city = 'Miasto jest wymagane';
    // „Działalność gospodarcza" jest wymagana TYLKO dla wielolokalowych (pytanie CEEB).
    if (form.type === 'wielolokalowy' && form.businessActivity === null) {
      errs.businessActivity = 'Wskaż, czy prowadzona jest działalność gospodarcza';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // #25: dokładne duplikaty adresu (ulica+nr+miasto, case-insensitive) wśród wyników
  // szukajki pipeline (group=all — budynki NOWE też się liczą; search po search_blob).
  // Fail-open: błąd sieci NIE blokuje dodawania (zwracamy pustą listę).
  // Pkt 27 (właściciel 2026-09-13): porównanie po stronie backendu, po znormalizowanym kluczu
  // (kolejność słów ulicy i zapis numeru bez znaczenia: „Janasa Wincentego 4 B” = „Wincentego
  // Janasa 4b”); `similar` = podobne adresy (literówki) jako podpowiedź. Fail-open jak dotąd.
  const findDuplicates = async (): Promise<DuplicateCandidate[]> => {
    try {
      const res = await buildingsApi.findDuplicates(form.street.trim(), form.houseNumber.trim(), form.city.trim());
      similarRef.current = res.similar || [];
      setSimilar(similarRef.current);
      return res.exact || [];
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('AddBuildingModal: sprawdzenie duplikatu nie powiodło się (fail-open)', e);
      return [];
    }
  };

  const handleSubmit = async (force = false, confirmDuplicateAddress = false) => {
    if (busy || terminal) return;
    if (!validate()) return;

    setResultMsg(null);

    // Krok 0 (#25) — wykrywanie duplikatu adresu. `force` = pomiń sprawdzenie („Utwórz mimo to" po
    // podpowiedzi `similar` albo potwierdzone „dodaj mimo to" przy duplikacie); duplikat (`exact`,
    // także `match: 'words'`) przejdzie przez serwer tylko z `confirmDuplicateAddress`.
    // Pkt 27: podobne adresy (literówka, inny numer) też zatrzymują PIERWSZY submit — pokazujemy
    // „Czy chodziło o…", a „Utwórz mimo to" tworzy budynek. Bez tego podpowiedź nigdy nie byłaby
    // widoczna (create szedł natychmiast po sprawdzeniu).
    if (!force) {
      setPhase('checking');
      const found = await findDuplicates();
      if (found.length > 0) {
        setDuplicates(found);
        setPhase('idle');
        return;
      }
      if (similarRef.current.length > 0) {
        setPhase('idle');
        return;
      }
    }
    setDuplicates(null);
    setDuplicateMsg(null);
    setConfirmDuplicate(false);
    setPhase('creating');

    // Krok 1 — POST /buildings. Błąd = nic nie powstało → brak onCreated (bezpieczny rollback).
    let buildingId: string;
    try {
      const base = {
        street: form.street.trim(),
        houseNumber: form.houseNumber.trim(),
        city: form.city.trim(),
        postalCode: form.postalCode.trim(),
        specialMarking: form.specialMarking.trim() || null,
        // 045 (§15): punkt z mapki (oba albo żaden — pojedyncza współrzędna jest bez sensu).
        geoLat: form.geoLat != null && form.geoLng != null ? form.geoLat : null,
        geoLng: form.geoLat != null && form.geoLng != null ? form.geoLng : null,
        type: form.type,
        // Wysyłamy odpowiedź TYLKO dla wielolokalowych; dla jednolokalowych zawsze null.
        businessActivity: form.type === 'wielolokalowy' ? form.businessActivity : null,
        noCommonHeatSource: false,
        heatSources: [],
        apartments: [],
      };
      // clientId pusty → pomijamy (kolumna nullable; '' nie jest poprawnym UUID).
      const payload = (form.clientId ? { ...base, clientId: form.clientId } : base) as Omit<
        Building,
        'id' | 'createdAt' | 'updatedAt'
      >;
      const created = confirmDuplicateAddress
        ? await buildingsApi.create(payload, { confirmDuplicate: true })
        : await buildingsApi.create(payload);
      buildingId = created.id;
      createdBuildingIdRef.current = created.id;
    } catch (e) {
      // #426: serwer sam odmawia duplikatu (409) — nic nie powstało; pokazujemy istniejący budynek.
      const err = e as BuildingCreateError;
      if (err && err.status === 409 && err.code === 'BUILDING_DUPLICATE_ADDRESS') {
        if (Array.isArray(err.existing) && err.existing.length > 0) {
          setDuplicates(err.existing);
          setDuplicateMsg(err.message || null);
        } else {
          setResultMsg({ text: err.message || 'Ten budynek już jest w bazie.', severity: 'error' });
        }
        setPhase('idle');
        return;
      }
      setResultMsg({ text: 'Nie udało się utworzyć budynku. Spróbuj ponownie.', severity: 'error' });
      setPhase('error');
      return;
    }

    // Właściciel 2026-09-13 (pkt 26): nowy budynek ma być NOWY, nie ZAPLANOWANY. Do 2026-09-13 modal
    // zakładał tu inspekcję PLANNED „żeby budynek był widoczny w widoku Aktywne" — skutek: resolver
    // dawał ZAPLANOWANY bez żadnego terminu, a przycisk [Zaplanuj] znikał. Teraz przegląd powstaje
    // dopiero przy planowaniu (PipelineView → [Zaplanuj]); widoczność załatwia PipelineView, który po
    // dodaniu przełącza filtr na NOWY i wpisuje adres w szukajkę.

    // Krok 3 — start importu CEEB; terminal obsłuży useEffect powyżej.
    setPhase('importing');
    awaitingImportRef.current = true;
    try {
      await start(buildingId);
    } catch (e) {
      // start() sam łapie błędy i ustawia status hooka='error' → useEffect zareaguje.
    }
  };

  const handleClose = () => {
    if (busy) return; // nie zamykaj w trakcie tworzenia/importu
    onClose();
  };

  const phaseLabel =
    phase === 'checking'
      ? 'Sprawdzanie adresu…'
      : phase === 'creating'
      ? 'Tworzenie budynku…'
      : phase === 'importing'
      ? 'Pobieranie danych z CEEB…'
      : '';

  // #426: jedno istniejące trafienie po skrócie ulicy (albo komunikat serwera z 409) czyta się jako
  // zdanie z adresem; ten sam adres (lub kilka wpisów) — jako lista z numerami CEEB.
  const dupWordsOnly = !!duplicates && duplicates.length > 0 && duplicates.every((d) => d.match === 'words');
  const dupHeadline = !duplicates || duplicates.length === 0
    ? ''
    : duplicateMsg
    ? duplicateMsg
    : dupWordsOnly && duplicates.length === 1
    ? `Ten budynek już jest w bazie: ${withCeeb(duplicates[0])} — otwórz go zamiast dodawać.`
    : `Budynek o tym adresie już istnieje w bazie${duplicates.length > 1 ? ` (${duplicates.length} wpisy)` : ''}:`;
  const dupShowList = !!duplicates && (duplicates.length > 1 || (!duplicateMsg && !dupWordsOnly));

  const msgColor =
    resultMsg?.severity === 'success'
      ? 'var(--status-ok)'
      : resultMsg?.severity === 'error'
      ? 'var(--status-deny)'
      : 'var(--text-primary)';
  const msgBg =
    resultMsg?.severity === 'success'
      ? 'var(--status-ok-bg)'
      : resultMsg?.severity === 'error'
      ? 'var(--status-deny-bg)'
      : 'var(--surface-fill)';
  const msgIcon =
    resultMsg?.severity === 'success' ? 'check' : resultMsg?.severity === 'error' ? 'triangle-alert' : 'check-circle';

  return (
    <>
    <ModalShell
      open={open}
      title="Dodaj nowy budynek"
      onClose={handleClose}
      width={560}
      footer={
        terminal ? (
          <Button variant="primary" onClick={handleClose}>Zamknij</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={busy}>Anuluj</Button>
            {duplicates && duplicates.length > 0 ? (
              // #426: duplikat blokuje domyślnie; obejście tylko przez pytanie (ConfirmModal) niżej.
              <>
                <Button variant="secondary" icon="triangle-alert" onClick={() => setConfirmDuplicate(true)} disabled={busy}>
                  {busy ? phaseLabel : 'To inna nieruchomość — dodaj mimo to'}
                </Button>
                {onShowExisting ? (
                  <Button
                    variant="primary"
                    icon="building"
                    onClick={() => onShowExisting({ id: duplicates[0].id, address: duplicates[0].address })}
                    disabled={busy}
                  >
                    Otwórz istniejący
                  </Button>
                ) : null}
              </>
            ) : similar.length > 0 ? (
              <Button variant="primary" icon="triangle-alert" onClick={() => handleSubmit(true)} disabled={busy}>
                {busy ? phaseLabel : 'Utwórz mimo to'}
              </Button>
            ) : (
              <Button variant="primary" icon="plus" onClick={() => handleSubmit()} disabled={busy}>
                {busy ? phaseLabel : 'Dodaj budynek'}
              </Button>
            )}
          </>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {/* Komunikat wyniku / błędu */}
        {resultMsg && (
          <div
            role={resultMsg.severity === 'error' ? 'alert' : 'status'}
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              background: msgBg,
              border: 'var(--border-width) solid var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              color: msgColor,
            }}
          >
            <Icon name={msgIcon as any} size={18} color={msgColor} />
            <span style={{ flex: 1 }}>{resultMsg.text}</span>
          </div>
        )}

        {/* Ostrzeżenie o duplikacie adresu (#25) */}
        {duplicates && duplicates.length > 0 && !terminal && (
          <div
            role="alert"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-4)',
              background: 'var(--status-rec-bg)',
              border: 'var(--border-width) solid var(--status-rec-bar)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              color: 'var(--status-rec)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 'var(--weight-semibold)' }}>
              <Icon name="triangle-alert" size={18} />
              <span data-testid="duplicate-headline">{dupHeadline}</span>
            </div>
            {dupShowList && (
              <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', color: 'var(--text-primary)' }}>
                {duplicates.map((d) => (
                  <li key={d.id}>
                    {d.address}
                    {d.ceeb_id ? <> — CEEB: {d.ceeb_id}</> : null}
                  </li>
                ))}
              </ul>
            )}
            {dupShowList && <span>To najpewniej ten sam budynek — otwórz istniejący wpis zamiast dodawać drugi.</span>}
          </div>
        )}
        {/* Pkt 27: podobne adresy (literówka, inny numer) — podpowiedź, nie blokada. */}
        {similar.length > 0 && !(duplicates && duplicates.length > 0) && !terminal && (
          <div
            role="status"
            data-testid="similar-addresses"
            style={{
              padding: 'var(--space-3) var(--space-4)',
              background: 'var(--surface-fill)',
              border: 'var(--border-width) solid var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              color: 'var(--text-secondary)',
            }}
          >
            Czy chodziło o: {similar.map((d) => `${d.address}${d.ceeb_id ? ` (CEEB ${d.ceeb_id})` : ''}`).join(' · ')}? Jeśli nie — „Utwórz mimo to”.
          </div>
        )}

        {/* Spinner w trakcie tworzenia/importu */}
        {busy && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-body)',
              color: 'var(--text-secondary)',
            }}
          >
            <style>{'@keyframes addb-spin { to { transform: rotate(360deg); } }'}</style>
            <span style={{ display: 'inline-flex', color: 'var(--action-primary)', animation: 'addb-spin 0.9s linear infinite' }}>
              <Icon name="refresh" size={20} />
            </span>
            <span>{phaseLabel}</span>
          </div>
        )}

        {/* Formularz (ukryty po terminalu) */}
        {!terminal && (
          <>
            {/* Zarządca (natywny select — brak DS Select) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <label
                htmlFor="add-building-client"
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-label)',
                  fontWeight: 'var(--weight-semibold)',
                  lineHeight: 'var(--leading-snug)',
                  color: 'var(--text-secondary)',
                }}
              >
                Zarządca (właściciel)
              </label>
              <select
                id="add-building-client"
                value={form.clientId}
                disabled={busy}
                onChange={(e) => setField('clientId', e.target.value)}
                style={{
                  width: '100%',
                  minHeight: 'var(--touch-min)',
                  padding: '0 var(--space-4)',
                  boxSizing: 'border-box',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-body)',
                  color: 'var(--text-primary)',
                  background: 'var(--surface-card)',
                  border: 'var(--border-width-strong) solid var(--border-strong)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <option value="">— bez zarządcy —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--space-3)' }}>
              <div style={{ flex: 2 }}>
                <InputField
                  label="Ulica"
                  value={form.street}
                  onChange={(e) => setField('street', e.target.value)}
                  required
                  autoFocus
                  disabled={busy}
                  errorMessage={fieldErrors.street}
                />
              </div>
              <div style={{ flex: 1 }}>
                {/* pkt 26: numer obowiązkowy TYLKO dla wielolokalowego (reguła w validate()); etykieta
                    jak na tablecie — „Numer (opcjonalnie)" dla jednolokalowego. */}
                <InputField
                  label={form.type === 'wielolokalowy' ? 'Numer' : 'Numer (opcjonalnie)'}
                  value={form.houseNumber}
                  onChange={(e) => setField('houseNumber', e.target.value)}
                  required={form.type === 'wielolokalowy'}
                  disabled={busy}
                  errorMessage={fieldErrors.houseNumber}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', gap: 'var(--space-3)' }}>
              <div style={{ flex: 1 }}>
                <InputField
                  label="Kod pocztowy"
                  value={form.postalCode}
                  onChange={(e) => setField('postalCode', e.target.value)}
                  required
                  disabled={busy}
                  errorMessage={fieldErrors.postalCode}
                />
              </div>
              <div style={{ flex: 2 }}>
                <InputField
                  label="Miasto"
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                  required
                  disabled={busy}
                  errorMessage={fieldErrors.city}
                />
              </div>
            </div>

            {/* Oznaczenie specjalne (§16, 2026-08-04) — opcjonalna nazwa własna przy adresie. */}
            <InputField
              label="Oznaczenie specjalne (opcjonalnie)"
              value={form.specialMarking}
              onChange={(e) => setField('specialMarking', e.target.value)}
              disabled={busy}
            />

            {/* 045 (§15, 2026-08-04): punkt budynku na mapie — zasila krok mapy rejestracji CEEB
                (najbardziej kruchy element rejestracji). Opcjonalny; bez punktu bot używa
                pinu z geokodera adresu, jak dotąd. */}
            <div>
              <Button variant="secondary" icon={showMap ? 'chevron-up' : 'map'} onClick={() => setShowMap((v) => !v)} disabled={busy}>
                {showMap ? 'Zwiń mapę' : 'Wskaż punkt na mapie (opcjonalnie)'}
              </Button>
              {form.geoLat != null && form.geoLng != null && (
                <span style={{ marginLeft: 'var(--space-3)', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                  Punkt: {form.geoLat}, {form.geoLng}
                  <Button variant="ghost" onClick={clearGeoPoint} disabled={busy} style={{ marginLeft: 'var(--space-2)' }}>Wyczyść</Button>
                </span>
              )}
              {showMap && (
                <div style={{ marginTop: 'var(--space-3)' }}>
                  <div ref={mapDivRef} style={{ height: 280, borderRadius: 'var(--radius-md)', border: 'var(--border-width) solid var(--border)' }} />
                  <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                    Kliknij na mapie, aby postawić punkt budynku (przybliż do dachu). Punkt trafi do rejestracji w CEEB.
                  </div>
                </div>
              )}
            </div>

            {/* Typ budynku — button-group (wzorzec BuildingInspectionView) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-label)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--text-secondary)',
                }}
              >
                Typ budynku
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                {TYPE_OPTIONS.map((opt) => {
                  const active = form.type === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      aria-pressed={active}
                      disabled={busy}
                      onClick={() => setType(opt.value)}
                      style={{
                        minWidth: 0,
                        padding: 'var(--space-3)',
                        textAlign: 'center',
                        cursor: busy ? 'not-allowed' : 'pointer',
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--text-label)',
                        fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
                        borderRadius: 'var(--radius-sm)',
                        border: `var(--border-width-strong) solid ${active ? 'var(--action-primary)' : 'var(--border)'}`,
                        background: active ? 'var(--changed-bg)' : 'var(--surface-fill)',
                        color: active ? 'var(--changed-text)' : 'var(--text-primary)',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Działalność gospodarcza — pytanie CEEB, WARUNKOWE: tylko wielolokalowy */}
            {form.type === 'wielolokalowy' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <span
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-label)',
                    fontWeight: 'var(--weight-semibold)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Działalność gospodarcza w budynku
                </span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                  {BUSINESS_ACTIVITY_OPTIONS.map((opt) => {
                    const active = form.businessActivity === opt.value;
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        aria-pressed={active}
                        disabled={busy}
                        onClick={() => setBusinessActivity(opt.value)}
                        style={{
                          minWidth: 0,
                          padding: 'var(--space-3)',
                          textAlign: 'center',
                          cursor: busy ? 'not-allowed' : 'pointer',
                          fontFamily: 'var(--font-sans)',
                          fontSize: 'var(--text-label)',
                          fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-regular)',
                          borderRadius: 'var(--radius-sm)',
                          border: `var(--border-width-strong) solid ${active ? 'var(--action-primary)' : 'var(--border)'}`,
                          background: active ? 'var(--changed-bg)' : 'var(--surface-fill)',
                          color: active ? 'var(--changed-text)' : 'var(--text-primary)',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {fieldErrors.businessActivity && (
                  <span
                    role="alert"
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--text-label)',
                      color: 'var(--status-deny)',
                    }}
                  >
                    {fieldErrors.businessActivity}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </ModalShell>
    {/* #426: świadome dodanie INNEJ nieruchomości pod adresem istniejącego budynku. */}
    <ConfirmModal
      open={open && confirmDuplicate && !!duplicates && duplicates.length > 0}
      title="Dodać drugi budynek pod tym adresem?"
      message={
        <>
          <span>W bazie jest już:</span>
          <ul data-testid="duplicate-confirm" style={{ margin: 'var(--space-2) 0', paddingLeft: 'var(--space-5)' }}>
            {(duplicates || []).map((d) => (
              <li key={d.id}>{d.address}{d.ceeb_id ? ` — CEEB: ${d.ceeb_id}` : ' — bez numeru CEEB'}</li>
            ))}
          </ul>
          <span>
            Dodaj tylko, jeśli to naprawdę inna nieruchomość (np. oficyna albo inna ulica o podobnej nazwie) —
            kopia tego samego budynku grozi zdublowaniem go w rejestrze CEEB. Dodanie zostanie zapisane w historii.
          </span>
        </>
      }
      confirmLabel="Tak, to inna nieruchomość — dodaj"
      cancelLabel="Wróć"
      destructive
      onConfirm={() => { setConfirmDuplicate(false); void handleSubmit(true, true); }}
      onCancel={() => setConfirmDuplicate(false)}
    />
    </>
  );
}

export default AddBuildingModal;
